import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// Rollen-Berechtigungen (muss mit Backend übereinstimmen)
const ROLE_PERMISSIONS = {
  admin: {
    can_create: true,
    can_edit: true,
    can_delete: true,
    can_view: true,
    can_manage_users: true,
    can_manage_settings: true,
    can_download_pdf: true,
    can_send_email: true,
  },
  user: {
    can_create: true,
    can_edit: true,
    can_delete: false,
    can_view: true,
    can_manage_users: false,
    can_manage_settings: false,
    can_download_pdf: true,
    can_send_email: true,
  },
  viewer: {
    can_create: false,
    can_edit: false,
    can_delete: false,
    can_view: true,
    can_manage_users: false,
    can_manage_settings: false,
    can_download_pdf: true,
    can_send_email: false,
  },
  buchhalter: {
    can_create: false,
    can_edit: true, // Nur Phase 4-5
    can_delete: false,
    can_view: true,
    can_manage_users: false,
    can_manage_settings: false,
    can_download_pdf: true,
    can_send_email: true,
    phases_allowed: [4, 5],
  },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try cookie-based session first, fall back to localStorage token
    const savedUser = localStorage.getItem('yh_user');
    const savedTenant = localStorage.getItem('yh_tenant');
    const token = localStorage.getItem('yh_token');

    if (savedUser) {
      // Restore UI state immediately from localStorage
      setUser(JSON.parse(savedUser));
      setTenant(savedTenant ? JSON.parse(savedTenant) : null);
    }

    if (token || savedUser) {
      // Validate session server-side (cookie or Bearer)
      api.get('/auth/me').then(res => {
        setUser(res.data.user);
        setTenant(res.data.tenant);
        localStorage.setItem('yh_user', JSON.stringify(res.data.user));
        localStorage.setItem('yh_tenant', JSON.stringify(res.data.tenant));
      }).catch(() => {
        // Session invalid – clear everything
        localStorage.removeItem('yh_token');
        localStorage.removeItem('yh_user');
        localStorage.removeItem('yh_tenant');
        setUser(null);
        setTenant(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback((token, userData, tenantData) => {
    // Store token for dev Bearer fallback; production relies on httpOnly cookie
    localStorage.setItem('yh_token', token);
    localStorage.setItem('yh_user', JSON.stringify(userData));
    localStorage.setItem('yh_tenant', JSON.stringify(tenantData));
    setUser(userData);
    setTenant(tenantData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors – clear local state regardless
    }
    localStorage.removeItem('yh_token');
    localStorage.removeItem('yh_user');
    localStorage.removeItem('yh_tenant');
    setUser(null);
    setTenant(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setTenant(res.data.tenant);
      localStorage.setItem('yh_user', JSON.stringify(res.data.user));
      localStorage.setItem('yh_tenant', JSON.stringify(res.data.tenant));
    } catch (e) {
      logout();
    }
  }, [logout]);

  // Berechtigungsprüfung
  const permissions = useMemo(() => {
    if (!user) return {};
    return ROLE_PERMISSIONS[user.role] || {};
  }, [user]);

  const hasPermission = useCallback((permission) => {
    return permissions[permission] || false;
  }, [permissions]);

  const canEditPhase = useCallback((phase) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'user') return true;
    if (user.role === 'buchhalter') {
      return [4, 5].includes(phase);
    }
    return false;
  }, [user]);

  const isAdmin = useMemo(() => user?.role === 'admin', [user]);
  const isViewer = useMemo(() => user?.role === 'viewer', [user]);
  const isBuchhalter = useMemo(() => user?.role === 'buchhalter', [user]);

  return (
    <AuthContext.Provider value={{
      user, tenant, loading, login, logout, refreshUser,
      permissions, hasPermission, canEditPhase,
      isAdmin, isViewer, isBuchhalter
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
